// Colour × size grid for a quote line — the rep picks colours from the ones
// the product really comes in and types a quantity under each size.
//
// Pilot finding 2026-10-13: the only way to give a line its sizes was a
// free-text form in the Table view, so the pilot's POs reached suppliers with
// no sizes. This editor sits on the default (document) view and in the Table
// view, and offers the catalogue's own colour and size names
// (GET /orders/:id/variant-options). A colour the catalogue doesn't list can
// still be typed ("Other colour…").
//
// Saving diffs the grid against the line's variants and calls the existing
// variant endpoints (POST new, PATCH changed, DELETE emptied), so the server's
// lock + ownership checks and quantity roll-up all still apply.
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import type { LineVariant } from "./VariantTable";
import type { SaleLine } from "@/components/catalog/SupplierSaleNote";

export interface ColourOption {
  name: string;
  sizes: string[];
}

export interface LineVariantOptions {
  lineId: string;
  colours: ColourOption[];
  needsBreakdown: boolean;
  /** SanMar sale prices (shown, never the cost). Absent from an older API. */
  saleLines?: SaleLine[];
}

export const variantOptionsQueryKey = (orderId: string): QueryKey => [
  "order-variant-options",
  orderId,
];

export async function fetchVariantOptions(orderId: string): Promise<LineVariantOptions[]> {
  const res = await api<{ lines: LineVariantOptions[] }>(
    `/orders/${orderId}/variant-options`,
  );
  return res.lines;
}

/** "" is the one-size column (variant.size null). */
const ONE_SIZE = "";
const OTHER = "__other__";
const cellKey = (colour: string, size: string): string => `${colour}\u0000${size}`;

interface Props {
  orderId: string;
  lineItemId: string;
  variants: LineVariant[];
  /** Catalogue colours for this line's product; empty for a product with no
   *  catalogue variants (custom items). */
  colours: ColourOption[];
  detailQueryKey: QueryKey;
  onClose: () => void;
  /** Called on the first change — the quote page keeps the editor open
   *  once the first save gives the line its colors. */
  onStartEditing?: () => void;
}

export function SizeBreakdownEditor({
  orderId,
  lineItemId,
  variants: allVariants,
  colours,
  detailQueryKey,
  onClose,
  onStartEditing,
}: Props) {
  const queryClient = useQueryClient();
  // A variant with a material ("Black / L / Bamboo") is left to the Table
  // view's variant list; the grid is colour × size only.
  const variants = useMemo(() => allVariants.filter((v) => !v.material), [allVariants]);

  // Columns: every size the product comes in, in wearing order, then any size
  // already on the line that the catalogue doesn't know. A one-size product
  // (tumbler, bag) gets a single "Qty" column.
  const sizeColumns = useMemo(() => {
    const cols: string[] = [];
    for (const c of colours) for (const s of c.sizes) if (!cols.includes(s)) cols.push(s);
    for (const v of variants) {
      const s = v.size ?? ONE_SIZE;
      if (!cols.includes(s)) cols.push(s);
    }
    return cols.length > 0 ? cols : [ONE_SIZE];
  }, [colours, variants]);

  const sizesFor = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const c of colours) m.set(c.name, new Set(c.sizes));
    return m;
  }, [colours]);

  const [rows, setRows] = useState<string[]>(() => {
    const seen: string[] = [];
    for (const v of variants) {
      const c = v.color ?? "";
      if (!seen.includes(c)) seen.push(c);
    }
    return seen;
  });
  const [cells, setCells] = useState<Map<string, string>>(() => {
    const m = new Map<string, string>();
    for (const v of variants) m.set(cellKey(v.color ?? "", v.size ?? ONE_SIZE), String(v.quantity));
    return m;
  });
  const [picking, setPicking] = useState("");
  const [otherName, setOtherName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unusedColours = colours.filter((c) => !rows.includes(c.name));

  const addRow = (name: string): void => {
    const clean = name.trim();
    if (!clean || rows.some((r) => r.toLowerCase() === clean.toLowerCase())) return;
    setRows([...rows, clean]);
    setPicking("");
    setOtherName("");
  };

  // A size the catalogue says this colour doesn't come in is greyed out, unless
  // the line already has a quantity there (never hide what's on the quote).
  const cellOffered = (colour: string, size: string): boolean => {
    const known = sizesFor.get(colour);
    if (!known || known.size === 0 || size === ONE_SIZE) return true;
    return known.has(size) || (cells.get(cellKey(colour, size)) ?? "") !== "";
  };

  const qtyOf = (colour: string, size: string): number =>
    Math.max(0, Math.floor(Number(cells.get(cellKey(colour, size)) ?? "") || 0));

  const total = rows.reduce(
    (sum, r) => sum + sizeColumns.reduce((s, c) => s + qtyOf(r, c), 0),
    0,
  );

  // Saves on its own a moment after a change (Stephanie 2026-10-26: "adding
  // the quantity to a color should automatically update the total units").
  // One save at a time; it always diffs against the line's LATEST variants
  // (refetched after each save), so a fast typist never double-adds a row.
  const latest = useRef({ variants, rows, cells });
  latest.current = { variants, rows, cells };
  const inFlight = useRef(false);
  const again = useRef(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const save = async (): Promise<void> => {
    if (inFlight.current) {
      again.current = true;
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setError(null);
    const base = `/orders/${orderId}/lines/${lineItemId}/variants`;
    const { variants, rows, cells } = latest.current;
    const qtyOf = (colour: string, size: string): number =>
      Math.max(0, Math.floor(Number(cells.get(cellKey(colour, size)) ?? "") || 0));
    try {
      const existing = new Map<string, LineVariant>();
      for (const v of variants) {
        const k = cellKey(v.color ?? "", v.size ?? ONE_SIZE);
        if (!existing.has(k)) existing.set(k, v);
      }
      const wanted = new Map<string, { colour: string; size: string; qty: number }>();
      for (const r of rows)
        for (const s of sizeColumns) {
          const qty = qtyOf(r, s);
          if (qty > 0) wanted.set(cellKey(r, s), { colour: r, size: s, qty });
        }
      // Add and change first, remove last: the line's quantity never drops to
      // zero mid-save.
      for (const [k, w] of wanted) {
        const v = existing.get(k);
        if (!v) {
          await api(base, {
            method: "POST",
            body: JSON.stringify({
              color: w.colour || undefined,
              size: w.size || undefined,
              quantity: w.qty,
            }),
          });
        } else if (v.quantity !== w.qty) {
          await api(`${base}/${v.id}`, {
            method: "PATCH",
            body: JSON.stringify({ quantity: w.qty }),
          });
        }
      }
      for (const v of variants) {
        const k = cellKey(v.color ?? "", v.size ?? ONE_SIZE);
        if (!wanted.has(k) || existing.get(k)?.id !== v.id) {
          await api(`${base}/${v.id}`, { method: "DELETE" });
        }
      }
      setSavedAt(Date.now());
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? "This quote is locked (invoiced), so sizes can't change."
          : "Couldn't save every size. Check the quantities and try again.",
      );
    } finally {
      // The line's units, price and variants come back fresh before the next save.
      await queryClient.refetchQueries({ queryKey: detailQueryKey }).catch(() => undefined);
      inFlight.current = false;
      setSaving(false);
      if (again.current) {
        again.current = false;
        void save();
      }
    }
  };

  // Typed / added / removed → save ~0.7 s later (not on first open).
  const changes = useRef(0);
  useEffect(() => {
    if (changes.current++ === 0) return;
    if (changes.current === 2) onStartEditing?.();
    const t = setTimeout(() => void save(), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, cells]);

  const sizeLabel = (s: string): string => (s === ONE_SIZE ? "Qty" : s);

  return (
    <div className="mt-3 rounded-md border border-rule bg-cream-50 p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Colors &amp; sizes
        </span>
        <span className="text-xs text-brown-400">
          Total <span className="font-semibold tabular-nums text-ink">{total.toLocaleString()}</span> units
        </span>
      </div>

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr className="text-left text-[11px] text-muted">
                <th className="py-1 pr-3 font-medium">Color</th>
                {sizeColumns.map((s) => (
                  <th key={s || "one"} className="px-1 py-1 text-center font-medium">
                    {sizeLabel(s)}
                  </th>
                ))}
                <th className="w-6" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r || "none"}>
                  <td className="whitespace-nowrap py-1 pr-3 text-ink">{r || "No color"}</td>
                  {sizeColumns.map((s) => {
                    const k = cellKey(r, s);
                    return (
                      <td key={s || "one"} className="px-1 py-1">
                        {cellOffered(r, s) ? (
                          <input
                            type="number"
                            min={0}
                            onFocus={(e) => e.currentTarget.select()}
                            inputMode="numeric"
                            value={cells.get(k) ?? ""}
                            onChange={(e) => {
                              const next = new Map(cells);
                              next.set(k, e.target.value);
                              setCells(next);
                            }}
                            aria-label={`${r || "No color"} ${sizeLabel(s)} quantity`}
                            className="w-14 rounded-md border border-rule bg-white px-1.5 py-1 text-right tabular-nums text-ink focus:border-ink focus:outline-none"
                          />
                        ) : (
                          <span
                            className="block w-14 text-center text-brown-200"
                            title={`${r} doesn't come in ${s}`}
                          >
                            —
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="py-1 pl-1">
                    <button
                      type="button"
                      onClick={() => setRows(rows.filter((x) => x !== r))}
                      aria-label={`Remove ${r || "this color"}`}
                      className="text-brown-300 hover:text-risk"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {colours.length > 0 && (
          <select
            value={picking}
            onChange={(e) => {
              if (e.target.value === OTHER) setPicking(OTHER);
              else addRow(e.target.value);
            }}
            aria-label="Add a color"
            className="h-8 rounded-md border border-rule bg-white px-2 text-xs text-ink"
          >
            <option value="">+ Add color…</option>
            {unusedColours.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
            <option value={OTHER}>Other color…</option>
          </select>
        )}
        {(colours.length === 0 || picking === OTHER) && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addRow(otherName);
            }}
            className="flex items-center gap-1"
          >
            <input
              autoFocus={picking === OTHER}
              value={otherName}
              onChange={(e) => setOtherName(e.target.value)}
              placeholder="Color name"
              maxLength={120}
              className="h-8 w-36 rounded-md border border-rule bg-white px-2 text-xs text-ink"
            />
            <Button type="submit" size="sm" variant="outline" disabled={!otherName.trim()}>
              <Plus className="h-3 w-3" /> Add
            </Button>
          </form>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-brown-400" aria-live="polite">
            {saving ? "Saving…" : savedAt ? "Saved ✓" : "Saves as you type"}
          </span>
          <Button type="button" size="sm" variant="outline" onClick={onClose} disabled={saving}>
            Done
          </Button>
        </div>
      </div>
      {error && <p className="mt-2 text-xs text-risk">{error}</p>}
    </div>
  );
}
